import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { RadioGroup, RadioGroupItem } from "../../../ui/radio-group";
import { Label } from "../../../ui/label";
import { Badge } from "../../../ui/badge";
import { Tooltip } from "../../../ui/tooltip";
import { 
  GraduationCapIcon, 
  BrainIcon,
  InfoIcon,
} from "lucide-react";

export type CompositionTypeValue = "SFT" | "RL";

interface CompositionTypeProps {
  compositionType: CompositionTypeValue;
  onChange: (type: CompositionTypeValue) => void;
}

const CompositionType: React.FC<CompositionTypeProps> = ({
  compositionType,
  onChange,
}) => {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <h2 className="text-2xl font-gc-demi-bold text-foreground">
          Choose Composition Type
        </h2>
        <p className="text-muted-foreground font-gc-reg">
          Select the training type for this dataset composition
        </p>
      </div>

      {/* Composition Type Selection */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <BrainIcon className="w-5 h-5 text-maitai-green" />
            Training Type
          </CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={compositionType}
            onValueChange={(value) => onChange(value as CompositionTypeValue)}
            className="space-y-6"
          >
            {/* SFT Option */}
            <div 
              className={`relative flex items-start space-x-4 p-4 rounded-lg border-2 transition-all cursor-pointer ${
                compositionType === "SFT" 
                  ? "border-maitai-green bg-maitai-green/5" 
                  : "border-border hover:border-muted-foreground/50"
              }`}
              onClick={() => onChange("SFT")}
            >
              <RadioGroupItem value="SFT" id="sft" className="mt-1" />
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-3">
                  <Label htmlFor="sft" className="text-base font-gc-med text-foreground cursor-pointer">
                    Supervised Fine-Tuning (SFT)
                  </Label>
                  <Badge variant="secondary" className="text-xs">
                    Standard
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground font-gc-reg">
                  Train your model on curated input-output pairs. Best for teaching specific behaviors, 
                  formatting, and domain knowledge.
                </p>
                <div className="flex items-start gap-2 pt-2">
                  <GraduationCapIcon className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <span className="text-xs text-muted-foreground">
                    Can include any dataset type with no strict pairing constraints
                  </span>
                </div>
              </div>
            </div>

            {/* RL Option */}
            <div 
              className={`relative flex items-start space-x-4 p-4 rounded-lg border-2 transition-all cursor-pointer ${
                compositionType === "RL" 
                  ? "border-maitai-green bg-maitai-green/5" 
                  : "border-border hover:border-muted-foreground/50"
              }`}
              onClick={() => onChange("RL")}
            >
              <RadioGroupItem value="RL" id="rl" className="mt-1" />
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-3">
                  <Label htmlFor="rl" className="text-base font-gc-med text-foreground cursor-pointer">
                    Reinforcement Learning (RL)
                  </Label>
                  <Badge variant="outline" className="text-xs border-amber-500 text-amber-600">
                    Advanced
                  </Badge>
                  <Tooltip 
                    content="RL compositions require paired datasets: Ground Truth (correct responses) and Adversarial (incorrect responses) with matching session IDs."
                    immediate
                  >
                    <InfoIcon className="w-4 h-4 text-muted-foreground cursor-help" />
                  </Tooltip>
                </div>
                <p className="text-sm text-muted-foreground font-gc-reg">
                  Train your model to distinguish between correct and incorrect responses. 
                  Requires paired Ground Truth and Adversarial datasets.
                </p>
                <div className="space-y-1 pt-2">
                  <div className="flex items-start gap-2">
                    <BrainIcon className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <span className="text-xs text-muted-foreground">
                      Only datasets tagged as <span className="font-gc-med">GROUND_TRUTH</span> or <span className="font-gc-med">ADVERSARIAL</span> are allowed
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <InfoIcon className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <span className="text-xs text-muted-foreground">
                      Datasets must have matching session IDs for proper pairing
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Info Card */}
      {compositionType === "RL" && (
        <Card className="border-amber-500/50 bg-amber-50/50 dark:bg-amber-950/20">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <InfoIcon className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="space-y-2">
                <p className="text-sm font-gc-med text-amber-800 dark:text-amber-200">
                  RL Composition Requirements
                </p>
                <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-1 list-disc list-inside">
                  <li>At least one dataset tagged as <span className="font-gc-med">GROUND_TRUTH</span></li>
                  <li>At least one dataset tagged as <span className="font-gc-med">ADVERSARIAL</span></li>
                  <li>Matching <span className="font-gc-med">session_id</span> values across paired requests</li>
                  <li>Same intent across paired requests</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CompositionType;
